import {
  isSameThing,
  type Nutrition,
  parseQuantity,
  productKey,
  type Quantity,
} from "@larder/shared";
import { doc, getDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import { db } from "../firebase";

/**
 * What the app already knows about a product, for something not yet owned.
 *
 * The shopping list holds intent, not items, so a cart line has no macros of
 * its own. But the household almost certainly has them anyway, in one of two
 * places, and neither costs a model call:
 *
 *   1. A PACKET THEY HAVE HAD BEFORE. Most of this list got here by running
 *      out of something, so the item that ran out is still in the log with its
 *      enrichment intact. Free, instant, already in memory.
 *   2. THE GLOBAL PRODUCT CACHE. Enrichment writes `products/{key}` once per
 *      product for the whole country (see functions/src/enrichment/enrich.ts),
 *      and the rules make it readable by anyone signed in. So a thing this
 *      household has never bought is still known, if anyone has bought it.
 *
 * Both are best-effort and the caller renders nothing when they come back
 * empty, which is the same state every item is in before enrichment lands.
 */
export interface ProductFacts {
  nutrition: Nutrition;
  /** How much is in one pack, for turning per-100g into per-packet. */
  size: Quantity | null;
}

/** Enough of an item to recognise one we have owned. */
export interface Known {
  id: string;
  name: string;
  nutrition: Nutrition | null;
  size: Quantity | null;
  purchasedAt: number;
}

export function useProductFacts(
  name: string,
  brand: string | null,
  sizeText: string | null,
  owned: readonly Known[],
): ProductFacts | null {
  const [fromCache, setFromCache] = useState<ProductFacts | null>(null);

  // The most recent packet of this thing we have actually had. Most recent
  // because enrichment improves over time and the newest answer is the best.
  let mine: Known | null = null;
  for (const i of owned) {
    if (!i.nutrition || !isSameThing(name, i.name)) continue;
    if (!mine || i.purchasedAt > mine.purchasedAt) mine = i;
  }

  const key = productKey(name, brand);

  useEffect(() => {
    // Only when the household's own history came up empty — a doc read is
    // cheap but it is not free, and the answer we already hold is better.
    if (mine || !key) {
      setFromCache(null);
      return;
    }
    let live = true;
    getDoc(doc(db, "products", key))
      .then((snap) => {
        if (!live || !snap.exists()) return;
        const d = snap.data() as { nutrition?: Nutrition | null; quantity?: Quantity };
        if (d.nutrition) {
          setFromCache({ nutrition: d.nutrition, size: d.quantity ?? null });
        }
      })
      .catch(() => {
        /* Nothing to say. The card simply does not appear. */
      });
    return () => {
      live = false;
    };
  }, [key, mine]);

  if (mine?.nutrition) {
    // The pack size printed on the entry wins over the one on an old item:
    // "400 g" beside the name on this list is about the packet being bought.
    return { nutrition: mine.nutrition, size: parseQuantity(sizeText) ?? mine.size };
  }
  return fromCache;
}
